import { ReactNode, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { EmptyStateConfig, InfinitePaginatedList, PaginatedList } from '@frontend/components';
import { InfiniteQueryResult } from '@frontend/types';
import { theme } from '@frontend/theme';
import { useDebouncedValue, useFormField } from '@frontend/design-system';
import { CompactTableContext, CompactTableFilterContext } from '../../context/compact-table-context';
import {
  CompactTableExpandAllButton,
  CompactTableExpandRowButton,
  CompactTableActions,
  CompactTableSearch,
  CompactTableSelectAllRows,
  CompactTableSelectRow,
  CompactTableRowActions,
  CompactTableBulkActions,
  CompactTableInfoRow,
  CompactTableExpandedRow,
  CompactTableRowHeader,
  CompactTableFilter,
  CompactTableToolbar,
} from './atoms';
import { CompactTableRow, CompactTableRowProps } from './compact-table-row';
import { useExpandableRows, useSelectableRows } from './hooks';
import {
  CompactTableActionsProps,
  CompactTableBulkActionsProps,
  CompactTableData,
  CompactTableManualFilters,
  RowSelectionConfig,
} from './types';

type CompactTableCommonProps<T extends CompactTableData> = {
  // memoize bulkActions
  bulkActions?: CompactTableBulkActionsProps<T>;
  // memoize tableActions
  tableActions?: CompactTableActionsProps;
  toolbarElement?: React.ReactNode;
  globalSearchConfig?: {
    initialValue?: string;
    placeholder?: string;
    // memoize search handler
    searchHandler: (value: string) => void;
    debounceDelay?: number;
  };
  emptyStateConfig?: Omit<EmptyStateConfig, 'description'> & { description?: ReactNode };
  isLoading: boolean;
  listHeight?: number | string;
  endOfListText?: ReactNode;
  tableId: string;
  isSelectable?: boolean;
  rowSelectionConfig?: RowSelectionConfig<T>;
  manualFiltersRender?: CompactTableManualFilters;
} & Omit<CompactTableRowProps<T>, 'item'>;

type CompactTableProps<T extends CompactTableData, K extends Record<'rows', T[]>> = CompactTableCommonProps<T> &
  (
    | {
        data?: never;
        infiniteQueryProps?: InfiniteQueryResult<K>;
      }
    | { data: T[]; infiniteQueryProps?: never }
  );

export const CompactTable = <T extends { id: string }, K extends Record<'rows', T[]>>({
  tableId,
  toolbarElement,
  globalSearchConfig,
  emptyStateConfig,
  tableActions,
  bulkActions,
  infiniteQueryProps,
  listHeight,
  endOfListText,
  isSelectable = true,
  data: dataProp,
  manualFiltersRender,
  isLoading,
  ...props
}: CompactTableProps<T, K>) => {
  const [showEnabledFiltersBadge, setShowEnabledFiltersBadge] = useState(false);

  const data = useMemo(
    () => infiniteQueryProps?.data?.pages.flatMap((page) => page.rows) || dataProp || [],
    [infiniteQueryProps, dataProp]
  );

  isSelectable = props.rowSelectionConfig?.onSelectionToggle ? isSelectable : false;
  const { expandedRows, toggleAllRowsExpanded, allExpandedByDefault, setAllExpandedByDefault, handleRowExpand } =
    useExpandableRows({ data });

  const { selectableRows, selectedRows, toggleSelectAllRows, areAllRowsSelected, handleRowSelect } = useSelectableRows({
    data,
    rowSelectionConfig: props.rowSelectionConfig,
  });

  // Global search logic
  const { searchHandler, placeholder, initialValue, debounceDelay = 500 } = globalSearchConfig || {};
  const searchFieldProps = useFormField({ type: 'text', value: initialValue || '', placeholder });
  const debouncedValue = useDebouncedValue(searchFieldProps.value, debounceDelay);
  useEffect(() => {
    searchHandler?.(debouncedValue);
  }, [searchHandler, debouncedValue]);

  const tableValues = useMemo(
    () => ({
      tableActions,
      bulkActions,
      data,
      selectedRows,
      toggleAllRowsExpanded,
      toggleSelectAllRows,
      allExpandedByDefault,
      setAllExpandedByDefault,
      selectableRows,
      areAllRowsSelected,
      searchFieldProps,
      tableId,
      isSelectable,
      isLoading,
    }),
    [
      tableActions,
      bulkActions,
      data,
      selectedRows,
      toggleAllRowsExpanded,
      toggleSelectAllRows,
      allExpandedByDefault,
      setAllExpandedByDefault,
      selectableRows,
      areAllRowsSelected,
      searchFieldProps,
      tableId,
      isSelectable,
      isLoading,
    ]
  );

  const filterValues = useMemo(
    () => ({
      showEnabledFiltersBadge,
      setShowEnabledFiltersBadge,
      manualFiltersRender,
    }),
    [showEnabledFiltersBadge, setShowEnabledFiltersBadge, manualFiltersRender]
  );

  return (
    <CompactTableContext.Provider value={tableValues}>
      <CompactTableFilterContext.Provider value={filterValues}>
        {toolbarElement && <div css={styles.toolbar}>{toolbarElement}</div>}
      </CompactTableFilterContext.Provider>
      <div css={styles.table}>
        {infiniteQueryProps ? (
          <InfinitePaginatedList
            infiniteQueryProps={infiniteQueryProps}
            renderListItem={({ listItem }) => (
              <CompactTableRow
                key={listItem.id}
                item={listItem}
                selectedRows={selectedRows}
                expandedRows={expandedRows}
                handleRowExpand={handleRowExpand}
                handleRowSelect={handleRowSelect}
                {...props}
              />
            )}
            endOfListText={endOfListText}
            height={listHeight}
            emptyStateConfig={emptyStateConfig as EmptyStateConfig}
          />
        ) : (
          <PaginatedList
            listData={data}
            renderListItem={({ listItem }) => (
              <CompactTableRow
                key={listItem.id}
                item={listItem}
                selectedRows={selectedRows}
                expandedRows={expandedRows}
                handleRowExpand={handleRowExpand}
                handleRowSelect={handleRowSelect}
                {...props}
              />
            )}
            endOfListText={endOfListText}
            height={listHeight ?? 400}
            emptyStateConfig={emptyStateConfig as EmptyStateConfig}
          />
        )}
      </div>
    </CompactTableContext.Provider>
  );
};

const styles = {
  table: css`
    margin-top: ${theme.spacing(2)};
  `,
  toolbar: css`
    margin-top: ${theme.spacing(2)};
  `,
};

CompactTable.Search = CompactTableSearch;
CompactTable.SelectRow = CompactTableSelectRow;
CompactTable.SelectAll = CompactTableSelectAllRows;
CompactTable.ExpandAll = CompactTableExpandAllButton;
CompactTable.TableActions = CompactTableActions;
CompactTable.ExpandRow = CompactTableExpandRowButton;
CompactTable.RowActions = CompactTableRowActions;
CompactTable.BulkActions = CompactTableBulkActions;
CompactTable.InfoRow = CompactTableInfoRow;
CompactTable.ExpandedRow = CompactTableExpandedRow;
CompactTable.RowHeader = CompactTableRowHeader;
CompactTable.Filter = CompactTableFilter;
CompactTable.Toolbar = CompactTableToolbar;
